import React, { Component } from "react";
import axios from "axios";
import store from "../../store/index";
import SpinnerLoader from "../../components/common/SpinnerLoader";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import Header from "../../components/admin/common/Header";
import Footer from "../../components/common/Footer";
import { Card, Row, Col, Alert, Form } from "react-bootstrap";

function formatMinor(amount, currency) {
  if (amount == null) return "—";
  const n = Number(amount) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

class ManageStripePayout extends Component {
  constructor(props) {
    super(props);
    this.authInfo = store.getState().auth.authInfo;
    this.state = {
      loading: true,
      saving: false,
      paying: false,
      payout_mode: "manual",
      payout_schedule: "weekly",
      balance: { available: [], pending: [] },
      stripePlatformAccountConfigured: false,
      payoutAmount: "",
      payoutCurrency: "",
      baseCurrencyFromSettings: "",
      stripeLive: null,
      lastPayoutError: null,
    };
    toast.configure();
  }

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = async () => {
    this.setState({ loading: true });
    try {
      const [sRes, gRes] = await Promise.all([
        axios.get("/admin/stripe/payout-settings", {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${this.authInfo.token}`,
          },
        }),
        axios.get("/admin/platform-settings/global", {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${this.authInfo.token}`,
          },
        }),
      ]);
      const baseFromGlobal =
        gRes.data?.status && gRes.data?.data?.baseCurrency
          ? String(gRes.data.data.baseCurrency).toLowerCase().slice(0, 3)
          : "";
      if (sRes.data.status && sRes.data.data) {
        const d = sRes.data.data;
        const avail = d.balance?.available || [];
        const primaryCc = avail[0]?.currency;
        const payoutCurrency = primaryCc || baseFromGlobal || "";
        this.setState({
          payout_mode: d.payout_mode || "manual",
          payout_schedule: d.payout_schedule || "weekly",
          balance: d.balance || { available: [], pending: [] },
          stripePlatformAccountConfigured: !!d.stripePlatformAccountConfigured,
          baseCurrencyFromSettings: baseFromGlobal,
          payoutCurrency,
          stripeLive: d.stripeLive || null,
        });
      } else {
        this.setState({ baseCurrencyFromSettings: baseFromGlobal });
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to load payout settings");
    } finally {
      this.setState({ loading: false });
    }
  };

  saveSettings = async (e) => {
    e.preventDefault();
    const { payout_mode, payout_schedule } = this.state;
    this.setState({ saving: true });
    try {
      const res = await axios.put(
        "/admin/stripe/payout-settings",
        { payout_mode, payout_schedule },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.authInfo.token}`,
          },
        }
      );
      if (res.data.status) {
        toast.success(res.data.message || "Saved");
        if (res.data.data?.stripeSync) {
          const s = res.data.data.stripeSync;
          if (s.ok) toast.info(s.message);
          else if (s.message) toast.warning(s.message);
        }
        this.loadSettings();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      this.setState({ saving: false });
    }
  };

  payOutNow = async () => {
    const { payoutCurrency, payoutAmount } = this.state;
    this.setState({ paying: true, lastPayoutError: null });
    try {
      const body = { currency: payoutCurrency };
      if (payoutAmount && String(payoutAmount).trim() !== "") {
        body.amount = parseFloat(String(payoutAmount).replace(/,/g, ""), 10);
      }
      const res = await axios.post("/admin/stripe/payout-now", body, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.authInfo.token}`,
        },
      });
      if (res.data.status) {
        toast.success("Payout initiated");
        this.setState({ payoutAmount: "" });
        this.loadSettings();
      }
    } catch (err) {
      const data = err.response?.data || {};
      const message = data.message || "Payout failed";
      toast.error(message);
      if (data.fallbackUrl) {
        this.setState({ lastPayoutError: { message, fallbackUrl: data.fallbackUrl } });
      }
    } finally {
      this.setState({ paying: false });
    }
  };

  render() {
    const {
      loading,
      saving,
      paying,
      payout_mode,
      payout_schedule,
      balance,
      stripePlatformAccountConfigured,
      payoutAmount,
      payoutCurrency,
      baseCurrencyFromSettings,
      stripeLive,
      lastPayoutError,
    } = this.state;

    const stripeInterval = stripeLive?.schedule?.interval || null;
    const stripeIsManual = stripeInterval === "manual";
    const stripeScheduleLabel = stripeInterval
      ? stripeInterval === "manual"
        ? "Manual (no automatic payouts)"
        : `${stripeInterval.charAt(0).toUpperCase()}${stripeInterval.slice(1)}` +
          (stripeLive?.schedule?.delay_days != null
            ? ` · ${stripeLive.schedule.delay_days}-day delay`
            : "") +
          (stripeLive?.schedule?.weekly_anchor
            ? ` · ${stripeLive.schedule.weekly_anchor}`
            : "") +
          (stripeLive?.schedule?.monthly_anchor
            ? ` · day ${stripeLive.schedule.monthly_anchor}`
            : "")
      : "Unknown";

    // Compare portal preference vs Stripe live to surface a warning banner.
    // Only meaningful if Stripe returned a schedule.
    let scheduleMismatch = null;
    if (stripeInterval) {
      if (payout_mode === "manual" && !stripeIsManual) {
        scheduleMismatch =
          `Portal preference is Manual but Stripe is set to ${stripeInterval}. ` +
          `Stripe will continue auto-paying out on its schedule regardless of this portal's mode.`;
      } else if (
        payout_mode === "auto" &&
        stripeInterval !== payout_schedule
      ) {
        scheduleMismatch =
          `Portal preference is ${payout_schedule} but Stripe's actual schedule is ${stripeInterval}. ` +
          `Update one of the two so they match.`;
      }
    }

    const stripeDashboardUrl =
      stripeLive?.dashboardPayoutUrl ||
      "https://dashboard.stripe.com/settings/payouts";

    const primary =
      balance.available && balance.available.length
        ? balance.available[0]
        : null;

    const currencyOptions =
      balance.available && balance.available.length
        ? balance.available
        : baseCurrencyFromSettings
          ? [{ currency: baseCurrencyFromSettings }]
          : [];

    return (
      <React.Fragment>
        {loading === true ? <SpinnerLoader /> : ""}
        <div className="seller_body">
          <Header />
          <Helmet>
            <title>Admin - Stripe payout schedule - Pay Earth</title>
          </Helmet>
          <div className="inr_top_page_title mb-2">
            <h2>Stripe payout schedule</h2>
          </div>
          <div className="seller_dash_wrap pt-2 pb-5">
            <div className="container">
              {loading ? null : (
                <Row className="mb-4">
                <Col md={8}>
                  {stripeLive?.ok && (
                    <Card className="shadow-sm mb-4">
                      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Stripe — live payout status</h5>
                        {stripeLive.livemode === false && (
                          <span className="badge bg-warning text-dark">Test mode</span>
                        )}
                      </Card.Header>
                      <Card.Body className="p-4">
                        <Row className="g-4">
                          <Col md={6}>
                            <div className="small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                              Stripe account
                            </div>
                            <div className="fw-semibold font-monospace mb-3" style={{ wordBreak: 'break-all' }}>
                              {stripeLive.accountId || "—"}
                            </div>
                            <div className="small text-muted">
                              Type: <strong className="text-dark">{stripeLive.accountType || "—"}</strong>
                              {stripeLive.country ? (
                                <> · Country: <strong className="text-dark">{stripeLive.country}</strong></>
                              ) : ""}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                              Stripe's actual payout schedule
                            </div>
                            <div className="fs-5 fw-semibold mb-3">
                              {stripeScheduleLabel}
                            </div>
                            <div className="small d-flex flex-wrap gap-3">
                              <span>
                                Charges:{" "}
                                <strong className={stripeLive.chargesEnabled ? "text-success" : "text-danger"}>
                                  {stripeLive.chargesEnabled ? "Enabled" : "Disabled"}
                                </strong>
                              </span>
                              <span>
                                Payouts:{" "}
                                <strong className={stripeLive.payoutsEnabled ? "text-success" : "text-danger"}>
                                  {stripeLive.payoutsEnabled ? "Enabled" : "Disabled"}
                                </strong>
                              </span>
                            </div>
                          </Col>
                        </Row>

                        {scheduleMismatch && (
                          <Alert variant="warning" className="mt-3 mb-0 small">
                            <strong>Heads up:</strong> {scheduleMismatch}{" "}
                            <a
                              href={stripeDashboardUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="fw-semibold"
                            >
                              Open Stripe payout settings →
                            </a>
                          </Alert>
                        )}

                        <div className="d-flex gap-2 mt-3 flex-wrap">
                          <a
                            href={stripeDashboardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm"
                          >
                            Open Stripe → Payout settings ↗
                          </a>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {stripeLive && stripeLive.ok === false && (
                    <Alert variant="danger" className="small">
                      Couldn't read Stripe's live payout schedule: {stripeLive.message}.{" "}
                      <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
                        Open Stripe dashboard →
                      </a>
                    </Alert>
                  )}

                  <Card className="shadow-sm mb-4">
                    <Card.Header className="bg-dark text-white">
                      <h5 className="mb-0">Payout mode</h5>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={this.saveSettings}>
                        <Form.Group className="mb-3">
                          <Form.Label>Mode</Form.Label>
                          <div>
                            <Form.Check
                              inline
                              type="radio"
                              name="payout_mode"
                              id="mode-manual"
                              label="Manual (Pay Out Now in this panel)"
                              checked={payout_mode === "manual"}
                              onChange={() => this.setState({ payout_mode: "manual" })}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              name="payout_mode"
                              id="mode-auto"
                              label="Automatic (Stripe sends on a schedule)"
                              checked={payout_mode === "auto"}
                              onChange={() => this.setState({ payout_mode: "auto" })}
                            />
                          </div>
                        </Form.Group>

                        {payout_mode === "auto" && (
                          <Form.Group className="mb-3">
                            <Form.Label>Schedule preference</Form.Label>
                            <Form.Select
                              value={payout_schedule}
                              onChange={(e) =>
                                this.setState({ payout_schedule: e.target.value })
                              }
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                              This is a local portal preference. For a Standard Stripe account, the actual payout schedule lives in Stripe Dashboard → Settings → Bank accounts and payouts.{" "}
                              <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
                                Open Stripe payout settings ↗
                              </a>
                            </Form.Text>
                          </Form.Group>
                        )}

                        <button
                          type="submit"
                          className="btn custom_btn btn_yellow"
                          disabled={saving}
                        >
                          {saving ? "Saving…" : "Save settings"}
                        </button>
                      </Form>
                    </Card.Body>
                  </Card>

                  {payout_mode === "manual" && (
                    <Card className="shadow-sm">
                      <Card.Header className="bg-secondary text-white">
                        <h5 className="mb-0">Balance &amp; manual payout</h5>
                      </Card.Header>
                      <Card.Body>
                        <p className="text-muted small">
                          Available balance is shown from Stripe (platform account). Leave amount
                          empty to pay out everything available for the selected currency.
                        </p>
                        {primary && (
                          <p className="fs-5">
                            <strong>Available:</strong>{" "}
                            {formatMinor(primary.amount, primary.currency)}
                          </p>
                        )}
                        {balance.available && balance.available.length > 1 && (
                          <ul className="small">
                            {balance.available.map((b, i) => (
                              <li key={i}>
                                {b.currency.toUpperCase()}: {formatMinor(b.amount, b.currency)}
                              </li>
                            ))}
                          </ul>
                        )}
                        {!primary && (
                          <p className="text-muted">No available balance reported for payout.</p>
                        )}

                        <Row className="g-2 align-items-end mt-3">
                          <Col md={3}>
                            <Form.Label>Currency</Form.Label>
                            <Form.Select
                              value={payoutCurrency}
                              onChange={(e) =>
                                this.setState({ payoutCurrency: e.target.value })
                              }
                              disabled={currencyOptions.length === 0}
                            >
                              {currencyOptions.length === 0 ? (
                                <option value="">Set platform base currency or load balance</option>
                              ) : (
                                currencyOptions.map((b) => (
                                  <option key={b.currency} value={b.currency}>
                                    {b.currency.toUpperCase()}
                                  </option>
                                ))
                              )}
                            </Form.Select>
                          </Col>
                          <Col md={4}>
                            <Form.Label>Amount (optional)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Full available if empty"
                              value={payoutAmount}
                              onChange={(e) => this.setState({ payoutAmount: e.target.value })}
                            />
                          </Col>
                          <Col md={5}>
                            <button
                              type="button"
                              className="btn btn-success mt-4 mt-md-0"
                              disabled={paying || !primary}
                              onClick={this.payOutNow}
                            >
                              {paying ? "Processing…" : "Pay out now"}
                            </button>
                          </Col>
                        </Row>

                        {lastPayoutError && (
                          <Alert variant="danger" className="mt-3 mb-0 small">
                            <strong>Payout couldn't be initiated via API.</strong>{" "}
                            {lastPayoutError.message}
                            <div className="mt-2">
                              <a
                                href={lastPayoutError.fallbackUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-danger"
                              >
                                Go to Stripe to Pay Out ↗
                              </a>
                            </div>
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </Col>

                <Col md={4}>
                  <Card className="shadow-sm mb-4">
                    <Card.Header className="bg-info text-white">
                      <h6 className="mb-0">How this works</h6>
                    </Card.Header>
                    <Card.Body className="p-4" style={{ lineHeight: 1.7 }}>
                      <Alert variant="light" className="small mb-4">
                        Funds settle to your <strong>platform</strong> Stripe balance. Automatic
                        bank payouts (including to Wise) are configured in{" "}
                        <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
                          Stripe → Settings → Bank accounts and payouts
                        </a>
                        .
                      </Alert>
                      <p className="small mb-2">
                        <strong>Portal vs Stripe:</strong> the mode toggle above is a local
                        preference. For <em>Standard</em> Stripe accounts the actual schedule is
                        locked to the Stripe Dashboard.
                      </p>
                      <p className="small text-muted mb-0">
                        Manual payouts use <code>payouts.create</code> against your platform
                        balance. Test mode uses fictional funds and routes to Stripe's test bank.
                      </p>
                    </Card.Body>
                  </Card>

                  <Card className="shadow-sm border-warning">
                    <Card.Header className="bg-warning text-dark">
                      <h6 className="mb-0">💡 Link Wise to lower FX fees</h6>
                    </Card.Header>
                    <Card.Body className="p-4" style={{ lineHeight: 1.7 }}>
                      <p className="small mb-2">
                        To avoid high FX fees when Stripe converts to your local currency, link your{" "}
                        <strong>Wise AUD / USD</strong> bank details directly as the default bank in
                        Stripe.
                      </p>
                      <ol className="small mb-4 ps-3" style={{ lineHeight: 1 }}>
                        <li className="mb-2">Open your live Wise account → AUD balance → Account details.</li>
                        <li className="mb-2">Copy the BSB + account number.</li>
                        <li>
                          Paste into{" "}
                          <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
                            Stripe → Bank accounts
                          </a>
                          , mark as default for AUD.
                        </li>
                      </ol>
                      <p className="small text-muted mb-0">
                        Every payout from here on will land in your Wise balance.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </React.Fragment>
    );
  }
}

export default ManageStripePayout;
